import type {
  CompanyIntegration,
  IntegrationSettings,
} from '@entities/integration';
import type { IntegrationSettingClient } from '@usecases/store-integration-settings.usecase';

import { BusIframe } from '@main/bus-layer/bus-iframe';
import type { BusEvent } from '@main/utils/bus-event-promisifier';
import { BusEventsPromisifier } from '@main/utils/bus-event-promisifier';
import type {
  BusRawScvIntegrationSetting,
  BusScvIntegrationSettingsPayload,
} from '@main/event-listeners/payloads/bus-integrations.payload';
import { ScvConfigStorage } from '@main/sf-connector/scv-config-storage';
import { Logger } from '@main/utils/logger';

/**
 * Bus-backed fetch of SCV integration settings.
 *
 * Sends API.GET_SCV_INTEGRATION_SETTINGS with the integration ids and receives
 * the raw GraphQL getScvIntegrationSettings array (sections is a JSON string).
 * This publisher parses/maps each raw setting to IntegrationSettings and keeps
 * only the settings that belong to the current salesforce org.
 */
export class FetchIntegrationSettingsPublisher
  implements IntegrationSettingClient
{
  private logger = new Logger();

  async fetchIntegrationSettings(
    integrations: CompanyIntegration[],
  ): Promise<IntegrationSettings[]> {
    const integrationIds = [...new Set(integrations.map(({ id }) => id))];

    const iframe = await BusIframe.getIframe();

    const event: BusEvent = {
      type: 'API.GET_SCV_INTEGRATION_SETTINGS',
      payload: { integrationIds },
    };

    this.logger.info('Publishing bus event', {
      action: event.type,
      integrationIds,
    });

    const response =
      await BusEventsPromisifier.promisifySyncEvent<BusScvIntegrationSettingsPayload>(
        {
          event,
          fulfilled: 'API.GET_SCV_INTEGRATION_SETTINGS_FULFILLED',
          rejected: 'API.GET_SCV_INTEGRATION_SETTINGS_REJECTED',
          target: iframe,
        },
      );

    const rawSettings = response ?? [];

    const settings = rawSettings.map(
      ({
        integrationId,
        instanceUrl,
        sections,
      }: BusRawScvIntegrationSetting): IntegrationSettings => ({
        integrationId: Number(integrationId),
        instanceUrl,
        sections: JSON.parse(sections),
      }),
    );

    /**
     * Return only those settings that belong to the current salesforce org.
     * Work around is to remove '-scrt' from the scrt url.
     */
    const salesforceOrgUrl: string = ScvConfigStorage.get().scrtUrl.replace(
      '-scrt',
      '',
    );

    return settings.filter(
      ({ instanceUrl }: IntegrationSettings) =>
        instanceUrl === salesforceOrgUrl,
    );
  }
}
